import React, { useEffect, useRef, useState } from 'react';
import {
  X, Sparkle, Download, ArrowsClockwise, MagicWand, Image as ImageIcon,
  Check, CalendarBlank, Clock, MapPin, PaperPlaneTilt
} from '@phosphor-icons/react';
import html2canvas from 'html2canvas';

/**
 * Invitation Generator Modal.
 * Two modes:
 *   - Template (default): HTML/CSS card rendered live with theme styling.
 *     Downloadable as PNG via html2canvas.
 *   - AI Enhance: replace template with a Nano Banana generated image.
 */
export default function InvitationGenerator({ event, theme, api, rsvpLink, onClose }) {
  const [aiImage, setAiImage] = useState(event?.invitation_image || null);
  const [mode, setMode] = useState(event?.invitation_image ? 'ai' : 'template');
  const [generating, setGenerating] = useState(false);
  const [extraPrompt, setExtraPrompt] = useState('');
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    setAiImage(event?.invitation_image || null);
    if (event?.invitation_image) setMode('ai');
  }, [event?.invitation_image]);

  const handleGenerateAI = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await api.post(`/events/${event.event_id}/invitation/generate`, {
        extra_prompt: extraPrompt,
      });
      setAiImage(res.data.image_data_url);
      setMode('ai');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to generate. Try again in a moment.');
    }
    setGenerating(false);
  };

  const handleDownloadTemplate = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });
      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${(event.title || 'invitation').replace(/[^a-z0-9]/gi, '_')}.png`;
      a.click();
    } catch (e) {
      console.error(e);
      setError('Download failed. Please try again.');
    }
    setDownloading(false);
  };

  const handleDownloadAI = () => {
    if (!aiImage) return;
    const a = document.createElement('a');
    a.href = aiImage;
    a.download = `${(event.title || 'invitation').replace(/[^a-z0-9]/gi, '_')}_AI.png`;
    a.click();
  };

  // ---- Web Share API: one-tap share invitation + RSVP link as a bundle ----
  const [sharing, setSharing] = useState(false);
  const canShareFiles = typeof navigator !== 'undefined'
    && typeof navigator.canShare === 'function'
    && typeof navigator.share === 'function';

  const getInvitationBlob = async () => {
    if (mode === 'ai' && aiImage) {
      const resp = await fetch(aiImage);
      return await resp.blob();
    }
    if (mode === 'template' && cardRef.current) {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null, scale: 2, useCORS: true,
      });
      return await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    }
    return null;
  };

  const handleShareInvitation = async () => {
    setError('');
    setSharing(true);
    try {
      const blob = await getInvitationBlob();
      if (!blob) {
        setError(mode === 'ai' ? 'Generate the AI invitation first.' : 'Invitation not ready.');
        setSharing(false);
        return;
      }
      const file = new File(
        [blob],
        `${(event.title || 'invitation').replace(/[^a-z0-9]/gi, '_')}.png`,
        { type: 'image/png' },
      );
      const shareText =
        `🎉 You're invited to *${event.title}*!\n\n` +
        `${event.event_date ? '📅 ' + formatDate(event.event_date) : ''}` +
        `${event.event_time ? ' at ' + event.event_time : ''}\n` +
        `${event.venue ? '📍 ' + event.venue + '\n' : ''}` +
        `\nPlease RSVP here:\n${rsvpLink}`;

      // 1) Best path: native Web Share with file (iOS, Android, modern desktop Safari/Chrome)
      if (canShareFiles && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            text: shareText,
            title: event.title,
            url: rsvpLink,
          });
          setSharing(false);
          return;
        } catch (err) {
          // AbortError = user cancelled, treat as no-op
          if (err && err.name === 'AbortError') {
            setSharing(false);
            return;
          }
          // fall through to fallback
        }
      }

      // 2) Fallback (desktop without Web Share API): auto-download PNG + open WhatsApp web with link & text
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      // Open WhatsApp share in new tab with prefilled text
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');
      setError('Image downloaded — attach it in the WhatsApp window we just opened.');
    } catch (e) {
      setError('Share failed. Try Download PNG and share manually.');
    }
    setSharing(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(rsvpLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-3 sm:p-4 overflow-y-auto"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid="invitation-generator-modal">
      <div className="relative w-full max-w-3xl my-4 sm:my-8" onClick={e => e.stopPropagation()}>
        <div className="rounded-3xl overflow-hidden shadow-2xl"
          style={{ background: 'rgba(20,20,20,0.95)', border: '1px solid rgba(255,255,255,0.08)' }}>

          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-white/5">
            <div className="flex items-center gap-2">
              <Sparkle size={18} weight="fill" style={{ color: theme.primary }} />
              <h3 className="font-bold text-white text-base sm:text-lg">Invitation Generator</h3>
            </div>
            <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="close-invitation-modal">
              <X size={20} />
            </button>
          </div>

          {/* Mode Toggle */}
          <div className="px-5 pt-4 flex gap-2">
            <button onClick={() => setMode('template')} data-testid="invitation-mode-template"
              className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-semibold transition-all ${mode === 'template' ? 'text-white' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}
              style={{
                background: mode === 'template' ? `${theme.primary}25` : 'rgba(255,255,255,0.03)',
                border: `1px solid ${mode === 'template' ? theme.primary + '50' : 'rgba(255,255,255,0.06)'}`,
              }}>
              <ImageIcon size={14} className="inline mr-1.5" /> Template Card
            </button>
            <button onClick={() => setMode('ai')} data-testid="invitation-mode-ai"
              className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-semibold transition-all ${mode === 'ai' ? 'text-white' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}
              style={{
                background: mode === 'ai' ? `${theme.primary}25` : 'rgba(255,255,255,0.03)',
                border: `1px solid ${mode === 'ai' ? theme.primary + '50' : 'rgba(255,255,255,0.06)'}`,
              }}>
              <MagicWand size={14} className="inline mr-1.5" /> AI Generated
            </button>
          </div>

          {/* Content */}
          <div className="p-5">
            {mode === 'template' && (
              <div className="flex flex-col items-center">
                <InvitationTemplate ref={cardRef} event={event} theme={theme} />
                <p className="text-[11px] text-neutral-500 mt-3 text-center">
                  Customize colours, emojis & message in the event editor (theme picker).
                </p>
              </div>
            )}

            {mode === 'ai' && (
              <div className="space-y-4">
                {aiImage ? (
                  <div className="flex flex-col items-center">
                    <div className="rounded-2xl overflow-hidden max-w-sm w-full"
                      style={{ boxShadow: `0 20px 60px ${theme.primary}40` }}>
                      <img src={aiImage} alt="AI generated invitation" className="w-full h-auto block" data-testid="ai-invitation-image" />
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl p-8 text-center"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.08)' }}>
                    <MagicWand size={36} className="mx-auto mb-3" style={{ color: theme.primary }} />
                    <p className="text-sm text-neutral-300 font-semibold mb-1">No AI invitation yet</p>
                    <p className="text-xs text-neutral-500">
                      Generate a unique invitation card using Gemini Nano Banana.
                    </p>
                  </div>
                )}

                <div>
                  <label className="text-[11px] text-neutral-400 mb-1.5 block font-medium">
                    Extra prompt (optional)
                  </label>
                  <textarea
                    value={extraPrompt}
                    onChange={e => setExtraPrompt(e.target.value)}
                    placeholder="e.g., Add a unicorn, include the message 'Splash & Splash'"
                    data-testid="ai-invitation-prompt"
                    className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none resize-none min-h-[60px]"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                  />
                </div>

                <button
                  onClick={handleGenerateAI}
                  disabled={generating}
                  data-testid="generate-ai-invitation-btn"
                  className="w-full py-3 rounded-xl text-white font-bold text-sm transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
                  style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`, boxShadow: `0 8px 30px ${theme.primary}40` }}>
                  {generating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating with AI...
                    </>
                  ) : (
                    <>
                      <ArrowsClockwise size={16} weight="bold" />
                      {aiImage ? 'Regenerate' : 'Generate AI Invitation'}
                    </>
                  )}
                </button>
                <p className="text-[10px] text-neutral-600 text-center">Powered by Gemini Nano Banana — takes 5-15 seconds</p>
              </div>
            )}

            {error && (
              <div className="mt-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-400">{error}</div>
            )}
          </div>

          {/* Action Bar */}
          <div className="p-5 border-t border-white/5 flex flex-wrap gap-2">
            {/* PRIMARY: One-tap Share Invitation (image + link + text bundled) */}
            <button
              onClick={handleShareInvitation}
              disabled={sharing || downloading || (mode === 'ai' && !aiImage)}
              data-testid="share-invitation-pack-btn"
              title={mode === 'ai' && !aiImage ? 'Generate the AI invitation first' : 'Share image + RSVP link in one tap'}
              className="flex-1 min-w-[160px] flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-white text-xs font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100"
              style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`, boxShadow: `0 8px 24px ${theme.primary}40` }}>
              {sharing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <PaperPlaneTilt size={14} weight="fill" />
                  {canShareFiles ? 'Share Invitation' : 'Share (Image + Link)'}
                </>
              )}
            </button>

            {mode === 'template' && (
              <button onClick={handleDownloadTemplate} disabled={downloading} data-testid="download-template-btn"
                className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10 disabled:opacity-50">
                <Download size={14} weight="bold" /> {downloading ? 'Saving...' : 'Download'}
              </button>
            )}
            {mode === 'ai' && aiImage && (
              <button onClick={handleDownloadAI} data-testid="download-ai-btn"
                className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">
                <Download size={14} weight="bold" /> Download
              </button>
            )}
            <button onClick={handleCopyLink} data-testid="copy-rsvp-link-btn"
              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">
              {copied ? <Check size={14} className="text-green-400" /> : <Sparkle size={14} />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
          {!canShareFiles && (
            <p className="px-5 pb-4 -mt-2 text-[10px] text-neutral-500">
              Tip: open this page on your phone for one-tap share to WhatsApp with image attached.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The actual invitation card — pure HTML/CSS so html2canvas can capture it.
 * Uses theme palette/decorations/emojis.
 */
const InvitationTemplate = React.forwardRef(({ event, theme }, ref) => {
  const emojis = theme.emojis || ['🎉'];
  const decorations = theme.decorations || [];

  return (
    <div
      ref={ref}
      data-testid="invitation-template-card"
      style={{
        width: 360,
        minHeight: 540,
        position: 'relative',
        background: theme.card_bg || `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
        borderRadius: 28,
        padding: 28,
        color: theme.text_color || '#FFFFFF',
        fontFamily: theme.font_family || 'Inter, system-ui, sans-serif',
        overflow: 'hidden',
        boxShadow: `0 25px 80px ${theme.primary}60`,
      }}>

      {/* Decorative corner flourishes */}
      <div style={{ position: 'absolute', top: 12, right: 18, fontSize: 28, opacity: 0.85 }}>{emojis[0]}</div>
      <div style={{ position: 'absolute', top: 14, left: 20, fontSize: 22, opacity: 0.7 }}>{emojis[1] || emojis[0]}</div>
      <div style={{ position: 'absolute', bottom: 24, right: 22, fontSize: 26, opacity: 0.85 }}>{emojis[2] || emojis[0]}</div>
      <div style={{ position: 'absolute', bottom: 30, left: 24, fontSize: 22, opacity: 0.7 }}>{emojis[3] || emojis[1] || emojis[0]}</div>

      {/* Pattern overlay */}
      {decorations.includes('polka') && <PolkaPattern />}
      {decorations.includes('sparkles') && <SparklePattern emojis={emojis} />}
      {decorations.includes('paw-prints') && <PawPattern />}
      {decorations.includes('waves') && <WavePattern color={theme.muted_text} />}
      {decorations.includes('stars') && <StarPattern />}

      {/* White inner card */}
      <div style={{
        position: 'relative',
        background: 'rgba(255,255,255,0.95)',
        borderRadius: 20,
        padding: '32px 24px',
        textAlign: 'center',
        marginTop: 40,
        marginBottom: 20,
        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
      }}>
        <div style={{ fontSize: 11, letterSpacing: 3, color: theme.primary, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>
          You're Invited
        </div>

        <h2 style={{
          fontSize: 28,
          fontWeight: 800,
          color: theme.primary,
          lineHeight: 1.15,
          margin: '4px 0 12px',
          letterSpacing: -0.5,
          fontFamily: theme.font_family || 'Inter, system-ui, sans-serif',
        }}>{event.title}</h2>

        {event.cover_message && (
          <p style={{ fontSize: 13, fontStyle: 'italic', color: '#525252', margin: '0 0 18px', lineHeight: 1.5 }}>
            "{event.cover_message}"
          </p>
        )}

        <div style={{ height: 1, background: `${theme.primary}22`, margin: '14px 0' }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center', color: '#262626' }}>
          {event.event_date && (
            <Row icon={<CalendarBlank size={14} weight="fill" color={theme.primary} />} text={formatDate(event.event_date)} />
          )}
          {event.event_time && (
            <Row icon={<Clock size={14} weight="fill" color={theme.primary} />}
              text={`${event.event_time}${event.end_time ? ' - ' + event.end_time : ''}`} />
          )}
          {event.venue && (
            <Row icon={<MapPin size={14} weight="fill" color={theme.primary} />} text={event.venue} multiline />
          )}
        </div>

        <div style={{ height: 1, background: `${theme.primary}22`, margin: '18px 0 14px' }} />

        <div style={{
          display: 'inline-block',
          padding: '7px 16px',
          borderRadius: 999,
          background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
          color: '#FFFFFF',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}>
          Please RSVP
        </div>
      </div>

      {/* Theme name footer */}
      <div style={{ textAlign: 'center', fontSize: 10, opacity: 0.85, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase' }}>
        {emojis.slice(0, 3).join('  ')} {theme.name} {emojis.slice(0, 3).reverse().join('  ')}
      </div>
    </div>
  );
});

function Row({ icon, text, multiline }) {
  return (
    <div style={{ display: 'flex', alignItems: multiline ? 'flex-start' : 'center', gap: 8, fontSize: 12.5, fontWeight: 600, lineHeight: 1.4, maxWidth: 280 }}>
      <span style={{ marginTop: multiline ? 2 : 0 }}>{icon}</span>
      <span style={{ textAlign: 'left' }}>{text}</span>
    </div>
  );
}

// Decorative patterns (CSS only, captureable by html2canvas)
function PolkaPattern() {
  return (
    <div style={{
      position: 'absolute', inset: 0, opacity: 0.18, pointerEvents: 'none',
      backgroundImage: 'radial-gradient(rgba(255,255,255,0.9) 2.5px, transparent 2.5px)',
      backgroundSize: '22px 22px',
    }} />
  );
}
function SparklePattern({ emojis }) {
  const positions = [{ t: 60, l: 30 }, { t: 100, r: 40 }, { t: 200, l: 24 }, { t: 320, r: 30 }, { b: 100, l: 60 }];
  return (
    <>
      {positions.map((p, i) => (
        <div key={i} style={{
          position: 'absolute',
          top: p.t, left: p.l, right: p.r, bottom: p.b,
          fontSize: 14, opacity: 0.45,
        }}>{emojis[i % emojis.length]}</div>
      ))}
    </>
  );
}
function PawPattern() {
  const positions = [{ t: 70, l: 26 }, { t: 130, r: 30 }, { b: 110, l: 28 }, { b: 80, r: 34 }];
  return (
    <>
      {positions.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', top: p.t, left: p.l, right: p.r, bottom: p.b,
          fontSize: 16, opacity: 0.38,
        }}>🐾</div>
      ))}
    </>
  );
}
function WavePattern({ color }) {
  return (
    <svg viewBox="0 0 360 80" style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 80, opacity: 0.45, pointerEvents: 'none' }}>
      <path d="M0,40 C90,80 180,0 270,40 C320,60 360,40 360,40 L360,80 L0,80 Z" fill={color || '#FFFFFF'} opacity="0.5" />
      <path d="M0,55 C90,30 180,75 270,55 C320,40 360,55 360,55 L360,80 L0,80 Z" fill={color || '#FFFFFF'} opacity="0.7" />
    </svg>
  );
}
function StarPattern() {
  const positions = [{ t: 60, l: 30 }, { t: 110, r: 40 }, { t: 240, l: 22 }, { b: 130, r: 32 }];
  return (
    <>
      {positions.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', top: p.t, left: p.l, right: p.r, bottom: p.b,
          fontSize: 14, opacity: 0.5, color: '#FFE082',
        }}>✦</div>
      ))}
    </>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return dateStr; }
}
